/**
 * Sibling colourway swatches on PLP product cards.
 *
 * In "swap" mode each dot carries its colourway product's first image, price and
 * URL inline (rendered by Liquid), so clicking a dot updates the card immediately
 * with no network request. The rest of the card state — the remaining gallery
 * images, the sale badge and the quick-add form — cannot be inlined: a card can
 * carry 12+ dots, and rendering every colourway's media and form into every card
 * would be paid for by every visitor, including those who never click a dot.
 * Those come from `?view=fv-card-swap` instead, fetched on intent (dot hover,
 * focus or tap) and cached for the session. In "navigate" mode the dots are plain
 * links and this element only handles expanding the "+N" overflow.
 *
 * Interaction is driven by pointer events (with a tap threshold) in addition to
 * click. Inside a carousel the dots sit in a horizontally scrollable track, and
 * on touch the browser can cancel the synthetic `click` when the finger moves
 * even slightly — but `pointerup` still fires for a tap, so we use it as the
 * primary trigger and de-dupe against the click that may follow.
 */

/** The theme's image width ladder — see snippets/product-media.liquid. */
const IMAGE_WIDTHS = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];

/** Mirrors `max_visible_slide_count` in snippets/card-gallery.liquid. */
const MAX_SLIDES = 5;

/**
 * Attributes Slideshow sets on itself mid-interaction. Cloning the live element
 * copies them, and the handlers that would clear them belong to the instance we
 * are throwing away, so they would stick on the replacement.
 */
const TRANSIENT_SLIDESHOW_ATTRIBUTES = ['transitioning', 'dragging', 'paused', 'refreshing-timeline', 'actioned'];

const STORAGE_KEY = 'fvCardSwapData';
const STORAGE_MAX_ENTRIES = 40;

/** @typedef {{ id?: string | number, src: string, ratio?: string | number, alt?: string, focalPoint?: string }} SwapMedia */
/** @typedef {{ id?: number, handle?: string, showArrows?: boolean, featuredMediaUrl?: string, media: SwapMedia[], badgesHtml: string, quickAddHtml: string, arrowsHtml: string }} SwapData */

/** @type {Map<string, SwapData>} */
const memory = new Map();
/** @type {Map<string, Promise<SwapData>>} */
const pending = new Map();
/** @type {Map<string, number>} */
const failures = new Map();
const MAX_FAILURES = 2;
let persist = true;

function shopRoot() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/**
 * On a size-filtered PLP the dot carries the sibling colourway's variant for
 * the shopper's size (resolved in `fluevog-card-swatch-dot`). Passing it here
 * is what makes the fetched quick-add form — its hidden `id`, its size-&-fit
 * line item property and its featured media — hold that size rather than the
 * colourway's first available one.
 * @param {string} handle
 * @param {string | null} [variantId]
 * @returns {string}
 */
function swapUrl(handle, variantId) {
  const params = new URLSearchParams({ view: 'fv-card-swap' });
  if (variantId) params.set('variant', variantId);
  const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
  if (preview) params.set('preview_theme_id', preview);
  return `${shopRoot()}products/${encodeURIComponent(handle)}?${params}`;
}

/**
 * Cache entries are keyed by the swap URL rather than the handle, so changing
 * the size facet mid-session misses rather than being served the previous
 * size's markup.
 * @param {string} key
 * @returns {SwapData | undefined}
 */
function readCache(key) {
  if (memory.has(key)) return memory.get(key);
  if (!persist) return undefined;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const data = JSON.parse(raw);
    if (Object.hasOwn(data, key)) {
      memory.set(key, data[key]);
      return data[key];
    }
  } catch {
    // private mode / corrupt payload
  }
  return undefined;
}

/**
 * @param {string} key
 * @param {SwapData} value
 */
function writeCache(key, value) {
  memory.set(key, value);
  if (!persist) return;
  try {
    const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    data[key] = value;
    // Each entry carries the quick-add markup, so bound the payload rather than
    // walking into a quota error on a deeply browsed session.
    const keys = Object.keys(data);
    for (const key of keys.slice(0, Math.max(0, keys.length - STORAGE_MAX_ENTRIES))) {
      delete data[key];
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Out of quota or unavailable — stay in memory for the rest of the page.
    persist = false;
  }
}

/**
 * Whether we should hold off on speculative fetches. The click itself always
 * fetches; this only suppresses the hover/focus prefetch.
 * @returns {boolean}
 */
function shouldSkipPrefetch() {
  const connection = /** @type {any} */ (navigator).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return ['slow-2g', '2g', '3g'].includes(connection.effectiveType);
}

/**
 * Rebuilds a Shopify CDN image URL across the theme's width ladder.
 * @param {string} base - An image URL carrying a `width` query param.
 * @returns {string}
 */
function buildSrcset(base) {
  let url;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    return '';
  }
  if (!url.searchParams.has('width')) return '';

  return IMAGE_WIDTHS.map((width) => {
    url.searchParams.set('width', String(width));
    return `${url.href} ${width}w`;
  }).join(', ');
}

/**
 * @param {string} html
 * @param {string} selector
 * @returns {Element | null}
 */
function parseFragment(html, selector) {
  if (!html) return null;
  const holder = document.createElement('div');
  holder.innerHTML = html;
  return holder.querySelector(selector);
}

/**
 * @param {string} text
 * @returns {SwapData | null}
 */
function parseSwapResponse(text) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const payload = doc.getElementById('fv-card-swap-data');
  if (!payload) return null;

  let data;
  try {
    data = JSON.parse(payload.textContent || '');
  } catch {
    return null;
  }
  if (!Array.isArray(data?.media)) return null;

  // A media entry with no renderable preview (an unprocessed video, say) would
  // otherwise become an <img src="null">.
  const media = data.media.filter((entry) => typeof entry?.src === 'string' && entry.src).slice(0, MAX_SLIDES);
  if (!media.length) return null;

  const template = (/** @type {string} */ id) => {
    const node = doc.getElementById(id);
    return node instanceof HTMLTemplateElement ? node.innerHTML : '';
  };

  return {
    ...data,
    media,
    badgesHtml: template('fv-card-swap-badges'),
    quickAddHtml: template('fv-card-swap-quick-add'),
    arrowsHtml: template('fv-card-swap-arrows'),
  };
}

/**
 * @param {string} handle
 * @param {string | null} [variantId]
 * @returns {Promise<SwapData>}
 */
function fetchSwap(handle, variantId) {
  const key = swapUrl(handle, variantId);
  const cached = readCache(key);
  if (cached !== undefined) return Promise.resolve(cached);

  const inFlight = pending.get(key);
  if (inFlight) return inFlight;

  // Every dot hover would otherwise retry a colourway that has no payload,
  // turning a broken product into a request storm.
  if ((failures.get(key) ?? 0) >= MAX_FAILURES) {
    return Promise.reject(new Error('fv-card-swap: giving up'));
  }

  const promise = (async () => {
    const response = await fetch(key, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`fv-card-swap ${response.status}`);

    const data = parseSwapResponse(await response.text());
    // A missing view template returns the full product page, which has no
    // payload. Fail closed: the card keeps the single-image state rather than
    // showing another colourway's images or form.
    if (!data) throw new Error('fv-card-swap: unexpected payload');

    writeCache(key, data);
    return data;
  })();

  pending.set(key, promise);
  promise
    .catch(() => failures.set(key, (failures.get(key) ?? 0) + 1))
    .then(() => pending.delete(key));
  return promise;
}

/** @typedef {{ html: string, className: string, style: string, productId: string | null, featuredMediaUrl: string | null, originalDot: Element | null }} GallerySnapshot */

/**
 * The original, Liquid-rendered state of a card gallery, captured before the
 * first swap so returning to the starting colourway costs no request.
 * @type {WeakMap<Element, GallerySnapshot>}
 */
const snapshots = new WeakMap();

class FluevogCardSwatches extends HTMLElement {
  /** @type {{ id: number, x: number, y: number, el: Element } | null} */
  #pointer = null;
  #handledByPointer = false;
  /** Bumped on every activation so a slow response for an earlier dot is dropped. */
  #token = 0;

  connectedCallback() {
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('click', this.#onClick);
    // `pointerover` rather than `pointerenter`: the dots are descendants and
    // `pointerenter` does not bubble.
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('focusin', this.#onFocusIn);
  }

  /**
   * The owning product card. Resolved lazily because the element can be cloned
   * or moved into a carousel after connection, which would stale a cached ref.
   * @returns {Element | null}
   */
  get card() {
    return this.closest('product-card');
  }

  get list() {
    return this.querySelector('.fv-card-swatches__list');
  }

  /**
   * Finds the nearest actionable control (a colour dot or the "+N" button).
   * @param {EventTarget | null} target
   * @returns {Element | null}
   */
  #actionable(target) {
    if (!(target instanceof Element)) return null;
    return target.closest('[data-fv-more], [data-product-url]');
  }

  /**
   * Finds the nearest swap-mode colour dot, which is the only control with a
   * colourway payload to prefetch.
   * @param {EventTarget | null} target
   * @returns {Element | null}
   */
  #swapDot(target) {
    if (!(target instanceof Element)) return null;
    const dot = target.closest('button[data-product-url][data-handle]');
    return dot && this.contains(dot) ? dot : null;
  }

  #onPointerDown = (event) => {
    this.#handledByPointer = false;
    const el = this.#actionable(event.target);
    this.#pointer = el ? { id: event.pointerId, x: event.clientX, y: event.clientY, el } : null;
    // A press is intent even on a metered connection, and on touch it is the
    // earliest signal we get — the browser may cancel the click entirely.
    this.#prefetch(this.#swapDot(event.target), { respectSaveData: false });
  };

  #onPointerOver = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#prefetch(this.#swapDot(event.target), { respectSaveData: true });
  };

  #onFocusIn = (event) => {
    this.#prefetch(this.#swapDot(event.target), { respectSaveData: true });
  };

  #onPointerUp = (event) => {
    const pointer = this.#pointer;
    this.#pointer = null;
    if (!pointer || event.pointerId !== pointer.id) return;

    // Ignore if the finger/mouse moved enough to be a scroll or drag, not a tap.
    if (Math.abs(event.clientX - pointer.x) > 10 || Math.abs(event.clientY - pointer.y) > 10) return;

    // The pointer must lift on the same control it started on.
    if (this.#actionable(event.target) !== pointer.el) return;

    this.#handledByPointer = true;
    this.#activate(pointer.el, event);
  };

  #onClick = (event) => {
    // The tap was already handled on pointerup; swallow the trailing click.
    if (this.#handledByPointer) {
      this.#handledByPointer = false;
      event.preventDefault();
      return;
    }

    const el = this.#actionable(event.target);
    if (!el) return;
    this.#activate(el, event);
  };

  /**
   * Warms the colourway cache without touching the DOM.
   * @param {Element | null} dot
   * @param {{ respectSaveData: boolean }} options
   */
  #prefetch(dot, { respectSaveData }) {
    const handle = dot?.getAttribute('data-handle');
    if (!handle) return;
    const variantId = dot?.getAttribute('data-variant-id');
    if (readCache(swapUrl(handle, variantId)) !== undefined) return;
    if (respectSaveData && shouldSkipPrefetch()) return;

    fetchSwap(handle, variantId).catch(() => {});
  }

  /**
   * Runs the action for an actionable control.
   * @param {Element} el - The "+N" button or a colour dot.
   * @param {Event} event
   */
  #activate(el, event) {
    // Expand the "+N" overflow.
    if (el.matches('[data-fv-more]')) {
      event.preventDefault();
      this.classList.add('is-expanded');
      return;
    }

    // Navigate-mode dots are anchors: let the browser follow the link. If the
    // click was cancelled (touch scroll) navigate manually from the pointer tap.
    if (el.tagName === 'A') {
      if (event.type === 'pointerup') {
        const href = el.getAttribute('href');
        if (href) window.location.href = href;
      }
      return;
    }

    event.preventDefault();
    this.#select(el);
  }

  /**
   * Swaps the whole card to the selected colourway: text, price, links, gallery,
   * sale badge and quick-add form.
   * @param {Element} dot
   */
  #select(dot) {
    // Resolve the owning card at activation time. Fall back to the nearest
    // card-like container so the swap still works inside carousels/sliders where
    // the <product-card> wrapper may not be matched.
    const card =
      this.card ??
      this.closest('.resource-list__slide, .product-grid__item, .resource-list__item, li');
    if (!card) return;

    const gallery = card.querySelector('.card-gallery');
    // Snapshot before any DOM or dot-state mutation, so the original colourway's
    // markup and its dot are captured as Liquid rendered them.
    const snapshot = gallery instanceof HTMLElement ? this.#snapshot(gallery) : null;

    const url = dot.getAttribute('data-product-url');
    const title = dot.getAttribute('data-title') || '';
    // The card shows the title with the colour descriptor stripped, so the swap uses
    // the same shortened form the Liquid-rendered title would have.
    const displayTitle = dot.getAttribute('data-display-title') || title;

    // Swap the price using the inline template rendered next to the dot.
    const priceTemplate = dot.parentElement?.querySelector('template.fv-card-swatches__price');
    const priceElement = card.querySelector('product-price');
    if (priceTemplate instanceof HTMLTemplateElement && priceElement) {
      priceElement.replaceChildren(priceTemplate.content.cloneNode(true));
    }

    // Swap the title text in place.
    if (displayTitle) {
      const titleLink = card.querySelector('a[ref="productTitleLink"]');
      const titleTarget =
        titleLink?.querySelector('.text-block p') ??
        titleLink?.querySelector('.text-block') ??
        titleLink;
      if (titleTarget) titleTarget.textContent = displayTitle;
    }

    // Keep the visually-hidden card link label in sync for screen readers.
    const linkLabel = card.querySelector('.product-card__link .visually-hidden');
    if (linkLabel && title) linkLabel.textContent = title;

    // Point every card link at the selected colourway.
    if (url) {
      const links = card.querySelectorAll(
        'a.product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]'
      );
      for (const link of links) {
        if (link instanceof HTMLAnchorElement) link.href = url;
      }
    }

    // Update the active dot state.
    for (const item of this.list?.querySelectorAll('.fv-card-swatches__item') ?? []) {
      item.classList.remove('is-current');
    }
    for (const other of this.list?.querySelectorAll('[data-product-url]') ?? []) {
      other.removeAttribute('aria-current');
    }
    dot.closest('.fv-card-swatches__item')?.classList.add('is-current');
    dot.setAttribute('aria-current', 'true');

    if (!(gallery instanceof HTMLElement) || !snapshot) return;

    const token = ++this.#token;

    // Back to where the card started: the original markup is already in hand.
    if (dot === snapshot.originalDot) {
      this.#restore(gallery, snapshot);
      return;
    }

    const handle = dot.getAttribute('data-handle');
    const variantId = dot.getAttribute('data-variant-id');
    const cached = handle ? readCache(swapUrl(handle, variantId)) : undefined;

    // Prefetched on hover/press, so this is the usual path — the full swap lands
    // in the same tick with no intermediate single-image state.
    if (cached) {
      this.#applyColourway(gallery, cached, token);
      return;
    }

    this.#applyDotOnly(gallery, dot);
    if (!handle) return;

    fetchSwap(handle, variantId)
      .then((data) => this.#applyColourway(gallery, data, token))
      .catch(() => {});
  }

  /**
   * @param {HTMLElement} gallery
   * @returns {GallerySnapshot}
   */
  #snapshot(gallery) {
    let snapshot = snapshots.get(gallery);
    if (!snapshot) {
      snapshot = {
        html: gallery.innerHTML,
        className: gallery.className,
        style: gallery.getAttribute('style') ?? '',
        productId: gallery.getAttribute('data-product-id'),
        featuredMediaUrl: this.closest('product-card-link')?.getAttribute('data-featured-media-url') ?? null,
        originalDot: this.list?.querySelector('[data-product-url][aria-current="true"]') ?? null,
      };
      snapshots.set(gallery, snapshot);
    }
    return snapshot;
  }

  /**
   * Restores the card's original, Liquid-rendered gallery. Replacing the markup
   * wholesale re-runs the custom elements inside it, so the slideshow, badge and
   * quick-add all come back correctly initialised.
   * @param {HTMLElement} gallery
   * @param {GallerySnapshot} snapshot
   */
  #restore(gallery, snapshot) {
    gallery.innerHTML = snapshot.html;
    gallery.className = snapshot.className;
    gallery.setAttribute('style', snapshot.style);
    if (snapshot.productId == null) gallery.removeAttribute('data-product-id');
    else gallery.setAttribute('data-product-id', snapshot.productId);
    this.#syncFeaturedMediaUrl(snapshot.featuredMediaUrl);
    this.#resetCardGalleryState();
  }

  /**
   * Interim state while the colourway payload is in flight: show the one image
   * the dot carries and nothing that could be wrong. Collapsing the gallery to a
   * single slide is what stops hover and the carousel arrows from revealing the
   * previous colourway's images.
   * @param {HTMLElement} gallery
   * @param {Element} dot
   */
  #applyDotOnly(gallery, dot) {
    const base = dot.getAttribute('data-image-base');
    const src = base || dot.getAttribute('data-image');

    if (src) {
      this.#buildSlideshow(gallery, [
        {
          src,
          alt: dot.getAttribute('data-image-alt') || '',
          ratio: dot.getAttribute('data-image-ratio') || '',
          focalPoint: dot.getAttribute('data-focal-point') || '',
        },
      ]);
    }

    // Sale state is unknown until the payload lands, so show no badge rather than
    // the previous colourway's.
    this.#applyBadges(gallery, '');
    // Likewise the quick-add form: adding to cart now would attach the original
    // product's variant and line item properties to the new colourway's line.
    const quickAdd = gallery.querySelector('quick-add-component');
    if (quickAdd instanceof HTMLElement) this.#disableQuickAdd(quickAdd);
    // Stale here would make the design-mode aspect ratio cache apply the previous
    // colourway's ratio; absent just falls back to measuring the image.
    gallery.removeAttribute('data-product-id');
    this.#resetCardGalleryState();
  }

  /**
   * @param {HTMLElement} gallery
   * @param {SwapData} data
   * @param {number} token
   */
  #applyColourway(gallery, data, token) {
    // A later dot was activated while this was in flight.
    if (token !== this.#token) return;
    if (!gallery.isConnected || !Array.isArray(data.media) || !data.media.length) return;

    this.#buildSlideshow(gallery, data.media, {
      // A single visible slide takes the slideshow's no-controls path, so arrows
      // would render but do nothing.
      showArrows: Boolean(data.showArrows) && data.media.length > 1,
      arrowsHtml: data.arrowsHtml,
    });
    this.#applyBadges(gallery, data.badgesHtml);
    this.#applyQuickAdd(gallery, data.quickAddHtml);

    if (data.id != null) gallery.setAttribute('data-product-id', String(data.id));
    this.#syncFeaturedMediaUrl(data.featuredMediaUrl ?? null);
    this.#resetCardGalleryState();
  }

  /**
   * Rebuilds the gallery slideshow for a colourway's media.
   *
   * The card's own slides are the template, so the classes, `sizes`, media fit and
   * timeline styles that card-gallery.liquid computed from the section and block
   * settings stay correct by construction. The whole <slideshow-component> is then
   * replaced rather than mutated: Slideshow picks its with-controls vs
   * without-controls path from the slide count at connect time and never re-runs
   * it, so a fresh element is the only way a changed slide count takes effect.
   *
   * @param {HTMLElement} gallery
   * @param {SwapMedia[]} mediaList
   * @param {{ showArrows?: boolean, arrowsHtml?: string }} [options]
   */
  #buildSlideshow(gallery, mediaList, { showArrows = false, arrowsHtml = '' } = {}) {
    const source = gallery.querySelector('slideshow-component');
    if (!source) return;

    const replacement = /** @type {HTMLElement} */ (source.cloneNode(true));
    for (const attribute of TRANSIENT_SLIDESHOW_ATTRIBUTES) replacement.removeAttribute(attribute);

    const scroller = replacement.querySelector('slideshow-slides');
    const container = replacement.querySelector('slideshow-container');
    if (!scroller || !container) return;

    const templates = /** @type {HTMLElement[]} */ ([...scroller.children].filter(
      (child) => child.tagName === 'SLIDESHOW-SLIDE'
    ));
    if (!templates.length) return;

    const media = mediaList.slice(0, MAX_SLIDES);
    const slides = media.map((entry, index) => {
      const template = templates[Math.min(index, templates.length - 1)];
      const slide = /** @type {HTMLElement} */ (template.cloneNode(true));
      this.#applySlideMedia(slide, entry, index);
      return slide;
    });

    scroller.replaceChildren(...slides);

    // Rebuild the scope list snippets/slideshow.liquid derives from slide_count.
    replacement.style.setProperty('--slideshow-timeline', media.map((_, index) => `--slide-${index}`).join(', '));
    replacement.setAttribute('initial-slide', '0');

    // With the 'adapt' ratio card-gallery.liquid pins --gallery-aspect-ratio to the
    // original product's featured media, and it overrides each slide's own --ratio.
    if (gallery.getAttribute('data-image-ratio') === 'adapt' && media[0]?.ratio) {
      gallery.style.setProperty('--gallery-aspect-ratio', String(media[0].ratio));
    }

    const arrows = container.querySelector('slideshow-arrows');
    if (showArrows && !arrows && arrowsHtml) container.insertAdjacentHTML('afterbegin', arrowsHtml);
    else if (!showArrows && arrows) arrows.remove();

    source.replaceWith(replacement);
  }

  /**
   * Points a cloned slide at a colourway's media.
   * @param {HTMLElement} slide
   * @param {SwapMedia} media
   * @param {number} index
   */
  #applySlideMedia(slide, media, index) {
    slide.removeAttribute('hidden');
    slide.removeAttribute('reveal');
    // The template slide may have been a hidden variant image.
    slide.removeAttribute('variant-image');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);

    const mediaId = media.id == null || media.id === '' ? null : String(media.id);
    // Slideshow.select({ id }) resolves against this attribute. With no id (the
    // interim single-slide state) the template's own id is left alone rather than
    // stripped, so resetVariant still has something to resolve.
    if (mediaId) slide.setAttribute('slide-id', mediaId);

    const wrapper = slide.querySelector('.product-media');
    if (wrapper instanceof HTMLElement) {
      if (media.ratio) wrapper.style.setProperty('--ratio', String(media.ratio));
      if (mediaId) wrapper.setAttribute('data-media-id', mediaId);
    }

    const image = slide.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    image.setAttribute('src', media.src);
    // `sizes` is deliberately left as rendered: card-gallery.liquid derives it
    // from the grid's column count and it does not change with the colourway.
    const srcset = buildSrcset(media.src);
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
    image.setAttribute('alt', media.alt || '');
    image.setAttribute('data-max-resolution', media.src);

    if (media.focalPoint) image.style.setProperty('--focal-point', media.focalPoint);
    else image.style.removeProperty('--focal-point');

    // Only the first slide keeps whatever loading/priority the card rendered with.
    if (index > 0) {
      image.setAttribute('loading', 'lazy');
      image.setAttribute('fetchpriority', 'low');
    }
  }

  /**
   * Swaps the sale badge. Only the inner badge changes: the container's classes
   * and custom properties come from theme settings and are already correct.
   * @param {HTMLElement} gallery
   * @param {string} html - Rendered product-card-badges markup, or '' to clear.
   */
  #applyBadges(gallery, html) {
    const container = gallery.querySelector('.product-badges');
    if (!container) return;

    const badge = parseFragment(html, '.product-badges');
    container.innerHTML = badge?.innerHTML ?? '';

    const position = [...container.classList]
      .find((name) => name.startsWith('product-badges--'))
      ?.replace('product-badges--', '');
    if (position) {
      gallery.classList.toggle(`card-gallery--badge-${position}`, container.children.length > 0);
    }
  }

  /**
   * Replaces the quick-add form. Patching only `input[name="id"]` is not enough:
   * the form embeds per-product line item properties (limited edition, presale,
   * size & fit) which would otherwise attach the original colourway's properties
   * to the new colourway's cart line.
   * @param {HTMLElement} gallery
   * @param {string} html - Rendered quick-add markup, or '' when unavailable.
   */
  #applyQuickAdd(gallery, html) {
    const existing = gallery.querySelector('quick-add-component');
    if (!(existing instanceof HTMLElement)) return;

    const replacement = parseFragment(html, 'quick-add-component');
    if (!replacement) {
      this.#disableQuickAdd(existing);
      return;
    }

    // The view template cannot know the PLP's section id, and cart section
    // rendering depends on it.
    const sectionId = existing.querySelector('product-form-component')?.getAttribute('data-section-id');
    const form = replacement.querySelector('product-form-component');
    if (sectionId && form) form.setAttribute('data-section-id', sectionId);

    existing.replaceWith(replacement);
  }

  /**
   * Makes the quick-add unusable rather than let it add the wrong product.
   * @param {HTMLElement} quickAdd
   */
  #disableQuickAdd(quickAdd) {
    quickAdd.hidden = true;
    const variantId = quickAdd.querySelector('input[name="id"]');
    if (variantId instanceof HTMLInputElement) variantId.disabled = true;
    for (const button of quickAdd.querySelectorAll('button')) {
      if (button instanceof HTMLButtonElement) button.disabled = true;
    }
  }

  /**
   * Keeps the PDP view transition's source image in sync.
   * @param {string | null} url
   */
  #syncFeaturedMediaUrl(url) {
    if (!url) return;
    this.closest('product-card-link')?.setAttribute('data-featured-media-url', url);
  }

  /**
   * The card remembers the slide the visitor last selected, which can point past
   * the end of a colourway with fewer images. Deferred a frame because the card's
   * refs are refreshed from a MutationObserver.
   */
  #resetCardGalleryState() {
    const card = this.card;
    if (!card) return;

    requestAnimationFrame(() => {
      const reset = /** @type {any} */ (card).resetGalleryState;
      if (typeof reset === 'function') reset.call(card);
    });
  }
}

if (!customElements.get('fluevog-card-swatches')) {
  customElements.define('fluevog-card-swatches', FluevogCardSwatches);
}
